import { Building2Icon } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useUser } from '@/components/auth/user-provider';
import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';
import { CustomerLogoMark } from '@/components/tenancy/customer-logo-mark';
import { selectedEntity, singleEntitySharesTenantName } from '@/features/tenancy/context-navigation';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export function TenantContextIndicator({ showLogo = true }: { showLogo?: boolean }) {
  const user = useUser();
  const { logo, header } = useCustomerSkin();
  const { pathname } = useLocation();
  const entities = user?.tenants ?? [];
  const entity = selectedEntity(entities, pathname);

  if (!entity) return null;
  const oneName = singleEntitySharesTenantName(entities, entity);

  return (
    <div
      aria-label={oneName ? `Tenant and entity ${entity.tenantName}` : `Tenant ${entity.tenantName}, entity ${entity.name}`}
      className={cn(
        'flex min-w-0 items-center gap-1.5 text-xs sm:text-sm',
        header ? 'customer-header-muted' : 'text-muted-foreground'
      )}
    >
      {showLogo && logo ? (
        <CustomerLogoMark variant="inline" alt="" className="shrink-0" />
      ) : showLogo && !oneName ? (
        <Building2Icon className="size-4 shrink-0" aria-hidden />
      ) : null}
      <span
        className={cn(
          oneName ? 'max-w-44 truncate sm:max-w-72' : 'max-w-24 truncate sm:max-w-40',
          oneName && (header ? 'customer-header-strong font-medium' : 'text-foreground font-medium')
        )}
        title={entity.tenantName}
      >
        {entity.tenantName}
      </span>
      {!oneName && (
        <>
          <span aria-hidden>—</span>
          <span
            className={cn('max-w-24 truncate font-medium sm:max-w-40', header ? 'customer-header-strong' : 'text-foreground')}
            title={entity.name}
          >
            {entity.name}
          </span>
        </>
      )}
      {entity.groupBindingIds?.length ? <Badge variant="outline" className="hidden shrink-0 text-xs sm:inline-flex" title="Last access check: verified direct or nested Microsoft group membership contributes organisation roles. Removing a group preserves independent individual access.">{entity.individualAccess ? 'Individual + group access' : 'Group access'}</Badge> : null}
    </div>
  );
}
